import { makeRequest } from './widget-facade';
import athenaConfig from '../config';
import AmplitudeUtils from './amplitude-utils';
import asyncRetry from 'async-retry';
import { ERROR_KEYS, ERROR_MESSAGES, API_REQUEST_RETRY_MIN_TIMEOUT } from '../constants';
import { indefiniteWaitPromise } from './common-utils';
import { getForgotPasswordLink } from './app-utils';

/**
 * Check if error is retryable (5xx server errors or no response)
 * Okta's invokeApiMethod returns errors with status in different places:
 * - error.xhr.status (wrapped errors)
 * - error.status (direct errors)
 * - error.responseJSON.status (some error formats)
 */
export function isRetryableError(error: any): boolean {
  if (!error) {
    return true; // No response - retry
  }

  const status = error?.xhr?.status || error?.status || error?.responseJSON?.status;
  // If status exists and is 5xx, it's retryable
  // If status doesn't exist, assume retryable (network error)
  if (status === undefined) {
    return true;
  }
  return status >= 500 && status <= 599;
}

/**
 * Generic retry wrapper for password reset API calls
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
async function makeRequestWithRetry({
  url,
  args,
  retryEventName,
  username,
}: {
  url: string;
  args: any;
  retryEventName: string;
  username: string;
}): Promise<any> {
  return asyncRetry(
    async (bail, attemptNumber) => {
      try {
        const result = await makeRequest({
          url,
          method: 'POST',
          args,
          headers: {
            'Content-Type': 'application/json',
          },
        });
        return result;
      }
      catch (error: any) {
        const status = error?.xhr?.status || error?.status || error?.responseJSON?.status;

        // Log retry attempt
        if (attemptNumber > 1) {
          AmplitudeUtils.logCustomEvent(retryEventName, {
            username,
            attemptNumber,
            status,
            isRetryable: isRetryableError(error),
          });
        }

        // If it's a retryable error (5xx), throw to trigger retry
        if (isRetryableError(error)) {
          throw error;
        }
        // Otherwise, bail out (don't retry)
        bail(error);
        return; // TypeScript needs this
      }
    },
    { retries: 2, factor: 1, minTimeout: API_REQUEST_RETRY_MIN_TIMEOUT }
  );
}

export function getTransformedError(error?: any) {
  const transformedError = error || {};
  if (error?.errorCode) {
    transformedError.errorSummary = ERROR_MESSAGES[error.errorCode];
  }
  else {
    transformedError.errorSummary = ERROR_MESSAGES[ERROR_KEYS.PASSWORD_RESET_LAMBDA_ERROR];
  }
  // Okta populates error banner with the message passed through error.errorSummary
  // https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/controllers/FormController.ts#L373
  return transformedError;
}

/**
   * Submits a form to the legacy password reset workflow
   * This is used to redirect users to step 2 of the legacy workflow
   * @param username - The username to submit
   * @returns A promise that never resolves (to prevent error flash before redirect)
   */
export function submitLegacyPasswordResetForm(username: string): Promise<void> {
  const legacyUrl = getForgotPasswordLink(settings.clientId || '');
  try {
    // Land the user on step 2 of the legacy workflow
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = legacyUrl!;

    // Create hidden input fields
    const fields = {
      USERNAME: username,
      STEP: 'USERNAME',
      NEXTSTEP: 'QUESTION',
      SUBMITVALUE: 'Next Step',
    };

    Object.entries(fields).forEach(([ name, value ]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();

    // Return a Promise that never resolves to prevent error flash before redirect
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return indefiniteWaitPromise();
  }
  catch (error) {
    AmplitudeUtils.logCustomEvent('PasswordReset::RedirectToLegacyWithUsernameFailed', {
      username,
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
      error,
    });
    window.location.href = legacyUrl!;
    // Return a Promise that never resolves to prevent error flash before redirect
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return indefiniteWaitPromise();
  }
}

export interface EligibilityCheckResponse {
  eligible: boolean;
  useLegacyWorkflow?: boolean;
  message?: string;
  errorCode?: string;
  // SSO user fields
  identityproviders?: Record<string, {
    contexts: Record<string, string>;
    nameid: string;
  }>;
}

/**
 * Initialize password reset flow - check user eligibility
 * Lambda will also assign user to appropriate Okta group
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
export async function userPasswordResetInit({
  username,
  supportEnv,
}: {
  username: string;
  supportEnv?: string;
}): Promise<EligibilityCheckResponse> {
  const response = await makeRequestWithRetry({
    url: `${athenaConfig.athenaOneResetPasswordLambdaBaseUrl}/user-password-reset-init`,
    args: { username, supportEnv },
    retryEventName: 'passwordResetInitRetry',
    username,
  });

  return {
    eligible: response.eligible,
    useLegacyWorkflow: response.useLegacyWorkflow,
    message: response.message,
    errorCode: response.errorCode,
    identityproviders: response.identityproviders,
  };
}

export interface PasswordResetResponse {
  success?: {
    name: string;
    href: string;
  };
  sessionToken?: string;
}

/**
 * Complete password reset by calling Lambda endpoint
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
export async function userPasswordReset({
  username,
  newPassword,
  stateHandle,
  symantecSecurityCode,
  supportEnv,
}: {
  username: string;
  newPassword: string;
  stateHandle: string;
  symantecSecurityCode?: string;
  supportEnv?: string;
}): Promise<PasswordResetResponse> {
  const response = await makeRequestWithRetry({
    url: `${athenaConfig.athenaOneResetPasswordLambdaBaseUrl}/user-password-reset`,
    args: {
      username,
      newPassword,
      stateHandle,
      symantecSecurityCode,
      supportEnv,
    },
    retryEventName: 'passwordResetCompletionRetry',
    username,
  });

  // Return the full response from the lambda (which contains the full IDX response from Okta)
  return response;
}

export interface SymantecVIPCheckResponse {
  shouldPrompt: boolean;
  username?: string;
}

/**
 * Check if user should be prompted for Symantec VIP code
 * Calls Lambda endpoint which validates username against stateHandle
 * and then determines if user is EPCS user
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
export async function shouldPromptForSymantecVIP({
  username,
  stateHandle,
}: {
  username: string;
  stateHandle: string;
}): Promise<SymantecVIPCheckResponse> {
  try {
    const response = await makeRequestWithRetry({
      url: `${athenaConfig.athenaOneResetPasswordLambdaBaseUrl}/should-prompt-for-symantec-vip`,
      args: {
        username,
        stateHandle,
      },
      retryEventName: 'passwordResetSymantecVIPCheckRetry',
      username,
    });

    return {
      shouldPrompt: response.shouldPrompt,
      username: response.username,
    };
  }
  catch (error: any) {
    // Log the error but don't throw - default to not prompting
    AmplitudeUtils.logCustomEvent('passwordResetSymantecVIPCheckFailed', {
      stateHandle: stateHandle.substring(0, 10) + '...', // Log partial for security
      error: error instanceof Error ? error.message : 'Unknown error',
    });

    // Default to not prompting for Symantec VIP if check fails
    return {
      shouldPrompt: false,
    };
  }
}

export interface ForgotUsernameResponse {
  success: boolean;
}

/**
 * Send forgot username email
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
export async function forgotUsername({
  email,
  supportEnv,
}: {
  email: string;
  supportEnv?: string;
}): Promise<ForgotUsernameResponse> {
  await makeRequestWithRetry({
    url: `${athenaConfig.athenaOneResetPasswordLambdaBaseUrl}/forgot-username`,
    args: { email, supportEnv },
    retryEventName: 'forgotUsernameRetry',
    username: email,
  });

  // If request succeeds (200 OK), return success
  return { success: true };
}
